import type { Dispatch } from 'react';
import { createContext } from 'react';

import type {
    CompositionValue,
    MaterialOnTheFlyValue,
} from '../../types/MaterialOnTheFlyValue';

type MaterialOnTheFlyAction =
    | {
          type: 'update_composition';
          payload: { composition: CompositionValue };
      }
    | {
          type: 'update_manufacture_detail';
          payload: {
              newManufactureDetail: MaterialOnTheFlyValue['manufactureDetail'];
          };
      }
    | {
          type: 'update_material_subcategory';
          payload: {
              newMaterialSubcategory: MaterialOnTheFlyValue['materialSubcategory'];
          };
      }
    | {
          type: 'update_weight_measure';
          payload: { newWeightMeasure: MaterialOnTheFlyValue['weightMeasure'] };
      }
    | {
          type: 'update_weight_unit';
          payload: { newWeightUnit: MaterialOnTheFlyValue['weightUnit'] };
      }
    | {
          type: 'update_weight_value';
          payload: { newWeightValue: MaterialOnTheFlyValue['weightValue'] };
      }
    | { type: 'add_composition' }
    | { type: 'clear_compositions' }
    | { type: 'clear_mof' }
    | { type: 'delete_composition'; payload: { feId: string } }
    | { type: 'initialize'; payload: MaterialOnTheFlyValue };

const emptyCompositionValue: CompositionValue = {
    feId: crypto.randomUUID(),
    rawMaterial: undefined,
    percentage: 100,
};

const initialMaterialOnTheFlyValue: MaterialOnTheFlyValue = {
    compositions: [emptyCompositionValue],
    manufactureDetail: null,
    materialOnTheFlyPublicId: null,
    materialSubcategory: null,
    weightValue: null, // Default value for weightValue
    weightUnit: null, // Default value for weightUnit
    weightMeasure: null, // Default value for weightMeasure
    usagePublicId: '', // Add a default value for usagePublicId
};

interface MaterialOnTheFlyValueContextType {
    materialOnTheFlyValue: MaterialOnTheFlyValue;
    dispatchMaterialOnTheFly: Dispatch<MaterialOnTheFlyAction>;
}

const MaterialOnTheFlyValueContext =
    createContext<MaterialOnTheFlyValueContextType>({
        materialOnTheFlyValue: initialMaterialOnTheFlyValue,
        dispatchMaterialOnTheFly: () => undefined,
    });

const materialOnTheFlyReducer = (
    state: MaterialOnTheFlyValue,
    action: MaterialOnTheFlyAction
): MaterialOnTheFlyValue => {
    switch (action.type) {
        case 'initialize':
            // Ensure every composition has a unique feId
            return {
                ...state,
                ...action.payload,
                compositions: (action.payload.compositions.length
                    ? action.payload.compositions
                    : [
                          {
                              feId: crypto.randomUUID(),
                              rawMaterial: undefined,
                              percentage: 100,
                          },
                      ]
                ).map((composition) => ({
                    ...composition,
                    feId: composition.feId,
                })),
            };
        case 'clear_mof':
            return {
                ...initialMaterialOnTheFlyValue,
                materialOnTheFlyPublicId: state.materialOnTheFlyPublicId,
            };
        case 'update_material_subcategory': {
            // When a MOF was saved without compositions (optional/forbidden),
            // and the user changes to a category where composition is shown again,
            // we need an initial row to render the fields.
            const compositionsWithInitialRow =
                state.compositions.length > 0
                    ? state.compositions
                    : [
                          {
                              feId: crypto.randomUUID(),
                              rawMaterial: undefined,
                              percentage: 100,
                          },
                      ];

            return {
                ...state,
                materialSubcategory: action.payload.newMaterialSubcategory,
                // Whenever we change material subcategory, we should clear the weight values
                weightValue: null,
                weightMeasure: null,
                weightUnit: null,
                // Clear manufacture detail as it depends on the subcategory
                manufactureDetail: null,
                // Reset raw materials in all compositions
                compositions: compositionsWithInitialRow.map((composition) => ({
                    ...composition,
                    rawMaterial: undefined,
                    percentage: undefined,
                })),
            };
        }
        case 'add_composition':
            return {
                ...state,
                compositions: [
                    ...state.compositions,
                    {
                        feId: crypto.randomUUID(), // Generate a unique feId
                        rawMaterial: undefined,
                        percentage: undefined,
                    },
                ],
            };
        case 'clear_compositions':
            return {
                ...state,
                compositions: [emptyCompositionValue],
            };
        case 'delete_composition':
            return {
                ...state,
                compositions: state.compositions.filter(
                    (composition: CompositionValue) =>
                        composition.feId !== action.payload.feId
                ),
            };
        case 'update_composition':
            // Update a single composition by feId, assuming feId is always present from initialize
            return {
                ...state,
                compositions: state.compositions.map((composition) =>
                    composition.feId !== action.payload.composition.feId
                        ? composition
                        : {
                              ...action.payload.composition,
                          }
                ),
            };
        case 'update_manufacture_detail':
            return {
                ...state,
                manufactureDetail: action.payload.newManufactureDetail,
            };
        case 'update_weight_measure':
            return {
                ...state,
                weightMeasure: action.payload.newWeightMeasure,
            };
        case 'update_weight_unit':
            return {
                ...state,
                weightUnit: action.payload.newWeightUnit,
            };
        case 'update_weight_value':
            return {
                ...state,
                weightValue: action.payload.newWeightValue,
            };

        default:
            return state;
    }
};

export {
    initialMaterialOnTheFlyValue,
    materialOnTheFlyReducer,
    MaterialOnTheFlyValueContext,
};
export type { MaterialOnTheFlyAction, MaterialOnTheFlyValue };
